import React, { useState, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Select,
  Icon,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { FaPlus, FaDownload, FaTrash, FaEye, FaFile, FaFilePdf, FaFileWord, FaFileImage, FaFileVideo, FaFileAudio, FaEdit } from "react-icons/fa";
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getClientFiles, uploadFile, downloadFile, deleteFile, updateFile } from '../services/fileService';
import { getClients } from '../services/clientService';
import toast from 'react-hot-toast';
import CustomModal from '../components/Modal';
import FileUploadForm from '../components/forms/FileUploadForm';
import { useAuth } from '../contexts/AuthContext';

function Files() {
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  
  // State برای فیلترها و modal
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const formRef = useRef();
  
  // React Query hooks
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  // دریافت لیست مراجعان (برای مشاوران، فقط مراجعان خودشان)
  const {
    data: clientsData,
    isLoading: isLoadingClients
  } = useQuery(
    ['clients', 'consultant'],
    () => getClients({ consultantId: user?.id }),
    {
      staleTime: 10 * 60 * 1000, // 10 دقیقه
      enabled: !!user?.id,
    }
  );
  
  // دریافت لیست فایل‌ها (برای مشاوران، فقط فایل‌های مراجعان خودشان)
  const {
    data: filesData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['files', selectedClientId, currentPage, searchTerm],
    () => getClientFiles(selectedClientId || 'all'),
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000, // 2 دقیقه
    }
  );

  // Mutation برای آپلود فایل
  const uploadFileMutation = useMutation(
    ({ clientId, fileData }) => uploadFile(clientId, fileData),
    {
      onSuccess: () => {
        toast.success('فایل با موفقیت آپلود شد');
        queryClient.invalidateQueries(['files']);
        setIsModalOpen(false);
      },
      onError: (error) => {
        toast.error(error.message || 'خطا در آپلود فایل');
      }
    }
  );

  // Mutation برای حذف فایل
  const deleteFileMutation = useMutation(deleteFile, {
    onSuccess: () => {
      toast.success('فایل با موفقیت حذف شد');
      queryClient.invalidateQueries(['files']);
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در حذف فایل');
    }
  });

  // Mutation برای بروزرسانی فایل
  const updateFileMutation = useMutation(updateFile, {
    onSuccess: () => {
      toast.success('فایل با موفقیت بروزرسانی شد');
      queryClient.invalidateQueries(['files']);
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در بروزرسانی فایل');
    }
  });

  // تابع آپلود فایل
  const handleUploadFile = (fileData) => {
    if (!selectedClientId) {
      toast.error('لطفاً ابتدا یک مراجع انتخاب کنید');
      return;
    }
    uploadFileMutation.mutate({ clientId: selectedClientId, fileData });
  };

  // تابع دانلود فایل
  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const blob = await downloadFile(fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('دانلود شروع شد');
    } catch (error) {
      toast.error('خطا در دانلود فایل');
    }
  };

  // تابع حذف فایل
  const handleDeleteFile = (fileId) => {
    if (window.confirm('آیا از حذف این فایل اطمینان دارید؟')) {
      deleteFileMutation.mutate(fileId);
    }
  };

  // تابع ویرایش فایل
  const handleEditFile = (file) => {
    const newCustomName = prompt('نام جدید فایل را وارد کنید:', file.customName || '');
    if (newCustomName !== null) {
      updateFileMutation.mutate({
        fileId: file._id,
        customName: newCustomName,
        description: file.description
      });
    }
  };

  // تابع باز کردن modal برای آپلود
  const handleAddFile = () => {
    if (!selectedClientId) {
      toast.error('لطفاً ابتدا یک مراجع انتخاب کنید');
      return;
    }
    setIsModalOpen(true);
  };

  // تابع بستن modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // تابع جستجو
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  // تابع تبدیل اندازه فایل
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // تابع دریافت آیکون فایل
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return FaFilePdf;
      case 'doc':
      case 'docx':
        return FaFileWord;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return FaFileImage;
      case 'mp4':
      case 'avi':
        return FaFileVideo;
      case 'mp3':
      case 'wav':
        return FaFileAudio;
      default:
        return FaFile;
    }
  };

  // اگر در حال بارگذاری است
  if (isLoading || isLoadingClients) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Flex justify="center" align="center" minH="400px">
              <Spinner size="xl" color="teal.500" />
              <Text mr={4} fontSize="lg" color={textColor}>
                در حال بارگذاری فایل‌ها...
              </Text>
            </Flex>
          </Box>
        </Box>
      </Flex>
    );
  }

  // اگر خطا رخ داده است
  if (error) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertTitle>خطا در بارگذاری داده‌ها!</AlertTitle>
              <AlertDescription>
                {error.message || 'خطا در ارتباط با سرور'}
              </AlertDescription>
            </Alert>
            <Button 
              mt={4} 
              colorScheme="teal" 
              onClick={() => refetch()}
              isLoading={isLoading}
            >
              تلاش مجدد
            </Button>
          </Box>
        </Box>
      </Flex>
    );
  }

  // داده‌های فایل‌ها
  const files = filesData?.data?.files || filesData?.files || filesData?.data || filesData || [];

  return (
    <>
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Box p="20px 0px 35px 0px">
              <Flex direction="column">
                <Text fontSize="lg" color={textColor} fontWeight="bold" me="auto">
                  مدیریت فایل‌ها
                </Text>
                <Text fontSize="md" fontWeight="400" me="auto" color="gray.400">
                  مشاهده و مدیریت فایل‌های مراجعان
                </Text>
              </Flex>
            </Box>
            <Box px="0px" pt="0px" pb="25px">
              <Flex px="25px" mb="25px" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={4}>
                <HStack spacing="15px" flexWrap="wrap">
                  {/* انتخاب مراجع */}
                  <Select
                    placeholder="انتخاب مراجع"
                    borderRadius="15px"
                    fontSize="sm"
                    maxW="250px"
                    value={selectedClientId || ''}
                    onChange={(e) => setSelectedClientId(e.target.value || null)}
                  >
                    <option value="">همه مراجعان</option>
                    {clientsData?.data?.clients?.map((client) => (
                      <option key={client._id} value={client._id}>
                        {client.firstName && client.lastName 
                          ? `${client.firstName} ${client.lastName}`
                          : client.name || 'نامشخص'}
                      </option>
                    ))}
                  </Select>
                  
                  <InputGroup maxW="300px">
                    <InputLeftElement>
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="جستجو در فایل‌ها..."
                      borderRadius="15px"
                      fontSize="sm"
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                  </InputGroup>
                </HStack>
                <Button
                  leftIcon={<FaPlus />}
                  colorScheme="teal"
                  borderRadius="15px"
                  fontSize="sm"
                  fontWeight="bold"
                  px="20px"
                  py="10px"
                  onClick={handleAddFile}
                >
                  آپلود فایل جدید
                </Button>
              </Flex>
              
              {/* جدول فایل‌ها */}
              <Box overflowX={{ sm: "scroll", xl: "hidden" }}>
                <Table variant="simple" color={textColor} size="sm">
                  <Thead>
                    <Tr my=".8rem" pl="0px" color="gray.400">
                      <Th borderColor={borderColor} textAlign="right">
                        نام فایل
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        مراجع
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        اندازه
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        تاریخ آپلود
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        عملیات
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {files.length === 0 ? (
                      <Tr>
                        <Td colSpan={5} textAlign="center" py={8}>
                          <Text color="gray.500">هیچ فایلی یافت نشد</Text>
                        </Td>
                      </Tr>
                    ) : (
                      files.map((file) => (
                        <Tr key={file._id || file.id}>
                          <Td minWidth={{ sm: "300px" }} pl="0px">
                            <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
                              <Icon
                                as={getFileIcon(file.originalName || file.fileName)}
                                boxSize="40px"
                                color="teal.500"
                                me="18px"
                              />
                              <Flex direction="column">
                                <Text
                                  fontSize="md"
                                  color={textColor}
                                  fontWeight="bold"
                                  minWidth="100%"
                                >
                                  {file.customName || file.originalName || file.fileName}
                                </Text>
                                <Text fontSize="sm" color="gray.400" fontWeight="normal">
                                  {file.description || 'بدون توضیحات'}
                                </Text>
                              </Flex>
                            </Flex>
                          </Td>
                          <Td>
                            <Text fontSize="md" color={textColor} fontWeight="bold">
                              {file.client?.firstName && file.client?.lastName
                                ? `${file.client.firstName} ${file.client.lastName}`
                                : file.client?.name || 'نامشخص'}
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize="md" color={textColor} fontWeight="bold">
                              {formatFileSize(file.fileSize || 0)}
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize="md" color={textColor} fontWeight="bold" pb=".5rem">
                              {new Date(file.createdAt).toLocaleDateString('fa-IR')}
                            </Text>
                          </Td>
                          <Td>
                            <HStack spacing="2">
                              <Button
                                size="sm"
                                colorScheme="blue"
                                variant="outline"
                                leftIcon={<FaDownload />}
                                onClick={() => handleDownloadFile(file._id || file.id, file.originalName || file.fileName)}
                              >
                                دانلود
                              </Button>
                              <Button
                                size="sm"
                                colorScheme="orange"
                                variant="outline"
                                leftIcon={<FaEdit />}
                                onClick={() => handleEditFile(file)}
                                isLoading={updateFileMutation.isLoading}
                              >
                                ویرایش
                              </Button>
                              <Button
                                size="sm"
                                colorScheme="red"
                                variant="outline"
                                leftIcon={<FaTrash />}
                                onClick={() => handleDeleteFile(file._id || file.id)}
                                isLoading={deleteFileMutation.isLoading}
                              >
                                حذف
                              </Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </Box>
        </Box>
      </Flex>

      {/* Modal آپلود فایل */}
      <CustomModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="آپلود فایل جدید"
        onSubmit={() => {
          const formData = formRef.current?.getValues();
          if (formData) {
            handleUploadFile(formData);
          }
        }}
        isLoading={uploadFileMutation.isLoading}
        submitText="آپلود"
        size="xl"
      >
        <FileUploadForm
          ref={formRef}
          onSubmit={handleUploadFile}
          isLoading={uploadFileMutation.isLoading}
        />
      </CustomModal>
    </>
  );
}

export default Files; 